package com.tovtalk.ui.platform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.savedstate.SavedState
import androidx.savedstate.read
import com.tovtalk.data.local.TokenManager
import com.tovtalk.utils.logger.Logger
import kotlinx.browser.window
import org.w3c.dom.events.Event
import org.w3c.dom.url.URLSearchParams

private const val TAG = "WebUrlSync"

/**
 * Bridges browser URL ↔ NavController on JS/Web.
 *
 * - On page load: parses the current URL and navigates NavController to the matching route.
 * - On NavController destination change: updates the browser URL via pushState/replaceState.
 * - On browser back/forward (popstate): navigates NavController to the parsed URL.
 */
@Composable
actual fun WebUrlSync(navController: NavController) {
    val router = remember { BrowserUrlRouter() }
    DisposableEffect(navController) {
        router.bind(navController)
        onDispose { router.unbind() }
    }
}

// ── SavedState helpers ──────────────────────────────────────────────────

private fun SavedState?.getString(key: String): String? =
    this?.read { getStringOrNull(key) }

private fun SavedState?.getInt(key: String): Int? =
    this?.read { getIntOrNull(key) }
        ?: this?.read { getStringOrNull(key) }?.toIntOrNull()

// ── BrowserUrlRouter ───────────────────────────────────────────────────

private class BrowserUrlRouter {

    private var navController: NavController? = null
    private var popstateListener: ((Event) -> Unit)? = null

    /**
     * True while we are programmatically navigating NavController from a URL change,
     * so we don't echo it back to the browser URL.
     */
    private var navigatingFromUrl = false

    /**
     * True while we are updating the browser URL from a NavController change,
     * so we don't re-enter from popstate.
     */
    private var updatingUrl = false

    /**
     * `addOnDestinationChangedListener` fires the listener synchronously on registration
     * with the current destination (SPLASH start destination). Without this flag we would
     * pushState("/splash") and overwrite the user's intended URL (e.g. /onboarding).
     */
    private var listenerInitialFireHandled = false

    // ── Bind / Unbind ───────────────────────────────────────────────

    fun bind(nc: NavController) {
        navController = nc
        listenerInitialFireHandled = false

        // Listen to NavController destination changes → update browser URL.
        // The first synchronous fire (for SPLASH start destination) is skipped in the listener.
        nc.addOnDestinationChangedListener(destinationListener)

        // Listen to browser popstate → navigate NavController.
        val listener: (Event) -> Unit = { _: Event ->
            if (!updatingUrl) {
                navigateToCurrentUrl()
            }
        }
        popstateListener = listener
        window.addEventListener("popstate", listener)

        // Initial URL handled by DeepLinkParser (in Main.kt, passed as pendingDeepLink) and
        // SplashScreen's routing logic — don't bypass them here. Calling navigateToCurrentUrl()
        // at bind time would cancel Splash's LaunchedEffect before translations/config/guest-token
        // finish loading, leaving the UI with missing strings.
    }

    fun unbind() {
        navController?.removeOnDestinationChangedListener(destinationListener)
        popstateListener?.let { window.removeEventListener("popstate", it) }
        navController = null
        popstateListener = null
    }

    // ── NavController → Browser URL ─────────────────────────────────

    private val destinationListener =
        NavController.OnDestinationChangedListener { _, destination, _ ->
            // Skip the synchronous initial fire (reports SPLASH start destination).
            // Keeping the user's original URL lets Splash run, load translations, and
            // then navigate to the real destination — which triggers the listener again
            // (this time not as the initial fire) and updates the URL correctly.
            if (!listenerInitialFireHandled) {
                listenerInitialFireHandled = true
                return@OnDestinationChangedListener
            }
            if (navigatingFromUrl) return@OnDestinationChangedListener
            val nc = navController ?: return@OnDestinationChangedListener

            val entry = nc.currentBackStackEntry ?: return@OnDestinationChangedListener
            val routePattern = entry.destination.route ?: return@OnDestinationChangedListener

            val browserUrl = navRouteToUrl(routePattern, entry)
            if (browserUrl != null) {
                val currentPath = window.location.pathname + window.location.search
                if (currentPath != browserUrl) {
                    updatingUrl = true
                    window.history.pushState(null, "", browserUrl)
                    updatingUrl = false
                    Logger.d("[WebUrlSync] URL → $browserUrl", TAG)
                }
            }
        }

    /**
     * Maps a NavController route pattern + back-stack entry to a browser-friendly URL.
     */
    private fun navRouteToUrl(routePattern: String, entry: NavBackStackEntry): String? {
        val args = entry.arguments

        return when {
            routePattern == "splash" -> "/splash"
            routePattern == "offline_launch" -> "/offline"
            routePattern == "onboarding" -> "/onboarding"

            routePattern.startsWith("home") -> {
                val tab = args.getInt("tab") ?: 0
                val playUserId = args.getInt("playUserId") ?: -1
                val playPostId = args.getInt("playPostId") ?: -1
                val startPostRef = args.getString("startPostRef") ?: ""
                if (tab == 0 && playUserId == -1 && playPostId == -1 && startPostRef.isBlank()) "/"
                else "/home?tab=$tab"
            }

            routePattern == "search" -> "/search"
            routePattern == "messages" -> "/messages"

            // profile_feed must be checked before generic profile/
            routePattern.startsWith("profile_feed/") -> {
                val userId = args.getString("userId") ?: return null
                val startPostId = args.getString("startPostId") ?: return null
                "/profile/$userId/reels/$startPostId"
            }

            routePattern.startsWith("profile/by/") -> {
                val username = args.getString("username") ?: return null
                "/@$username"
            }

            routePattern.startsWith("profile/") && routePattern.contains("followers") -> {
                val userId = args.getString("userId") ?: return null
                "/profile/$userId/followers"
            }

            routePattern.startsWith("profile/") && routePattern.contains("following") -> {
                val userId = args.getString("userId") ?: return null
                "/profile/$userId/following"
            }

            routePattern.startsWith("profile/") -> {
                val userId = args.getString("userId") ?: return null
                "/profile/$userId"
            }

            routePattern == "edit_profile" -> "/edit-profile"
            routePattern == "settings" -> "/settings"
            routePattern == "sessions" -> "/settings/sessions"
            routePattern == "blocked_users" -> "/settings/blocked"
            routePattern == "content_upload_management" -> "/settings/content-upload"
            routePattern == "notification_settings" -> "/notification-settings"

            routePattern.startsWith("post/") -> {
                val postRef = args.getString("postRef") ?: return null
                "/post/$postRef"
            }

            routePattern.startsWith("hashtag/") -> {
                val tag = args.getString("tag") ?: return null
                "/hashtag/$tag"
            }

            routePattern.startsWith("chat/") -> {
                val receiverId = args.getString("receiverId") ?: return null
                "/chat/$receiverId"
            }

            else -> null
        }
    }

    // ── Browser URL → NavController ─────────────────────────────────

    private fun navigateToCurrentUrl(replace: Boolean = false) {
        val nc = navController ?: return
        val pathname = window.location.pathname.trim().ifBlank { "/" }
        val search = window.location.search
        val segments = pathname.trim('/').split('/').filter { it.isNotBlank() }
        val q = URLSearchParams(search)

        val navRoute = urlToNavRoute(segments, q) ?: return

        navigatingFromUrl = true
        try {
            nc.navigate(navRoute) {
                launchSingleTop = true
            }
            Logger.d("[WebUrlSync] URL '$pathname' → nav '$navRoute'", TAG)
        } catch (e: Exception) {
            Logger.e("[WebUrlSync] Failed to navigate to '$navRoute': ${e.message}", TAG)
        } finally {
            navigatingFromUrl = false
        }
    }

    /**
     * Parses URL segments + query params into a NavController route string
     * matching TovTalkNavHost composable() declarations.
     */
    private fun urlToNavRoute(segments: List<String>, q: URLSearchParams): String? {
        // Root "/" → home
        if (segments.isEmpty()) {
            val ref = q.get("startPostRef")?.toString()?.takeIf { it.isNotBlank() } ?: ""
            return "home?tab=0&playUserId=-1&playPostId=-1&startPostRef=$ref"
        }

        return when (segments[0]) {
            "splash" -> "splash"
            "offline" -> "offline_launch"
            "onboarding" -> "onboarding"

            // Legacy routes → home
            "language", "kashrut", "consumption", "login", "register" ->
                "home?tab=0&playUserId=-1&playPostId=-1&startPostRef="

            "home" -> {
                val tab = q.get("tab")?.toString()?.toIntOrNull() ?: 0
                val playUserId = q.get("playUserId")?.toString()?.toIntOrNull() ?: -1
                val playPostId = q.get("playPostId")?.toString()?.toIntOrNull() ?: -1
                val startPostRef = q.get("startPostRef")?.toString()?.takeIf { it.isNotBlank() } ?: ""
                "home?tab=$tab&playUserId=$playUserId&playPostId=$playPostId&startPostRef=$startPostRef"
            }

            "search" -> "search"
            "messages" -> "messages"

            "settings" -> when {
                segments.size >= 2 && segments[1] == "sessions" -> "sessions"
                segments.size >= 2 && segments[1] == "blocked" -> "blocked_users"
                segments.size >= 2 && segments[1] == "content-upload" -> "content_upload_management"
                else -> "settings"
            }
            "sessions" -> "sessions"
            "blocked-users" -> "blocked_users"

            "u" -> if (segments.size >= 2) "profile/by/${segments[1]}" else null

            "p" -> if (segments.size >= 2)
                "home?tab=0&playUserId=-1&playPostId=-1&startPostRef=${segments[1]}"
            else null

            "post" -> if (segments.size >= 2) "post/${segments[1]}" else null

            "hashtag", "tag" -> if (segments.size >= 2) "hashtag/${segments[1]}" else null

            "chat" -> if (segments.size >= 2) "chat/${segments[1]}/" else null

            "notifications" -> "messages"
            "notification-settings" -> "notification_settings"
            "edit-profile" -> "edit_profile"

            "profile" -> when {
                segments.size >= 2 && segments[1] == "me" -> {
                    val uid = TokenManager.userId ?: 0
                    "profile/$uid/-1"
                }
                segments.size >= 3 && segments[1] == "by" -> "profile/by/${segments[2]}"
                segments.size >= 4 && segments[2] == "reels" ->
                    "profile_feed/${segments[1]}/${segments[3]}"
                segments.size >= 2 -> {
                    val fromPost = segments.getOrNull(2) ?: "-1"
                    "profile/${segments[1]}/$fromPost"
                }
                else -> null
            }

            else -> {
                // /@username shortlinks
                val first = segments[0]
                if (first.startsWith("@") && first.length > 1) {
                    "profile/by/${first.substring(1)}"
                } else {
                    Logger.w("[WebUrlSync] Unknown URL: /${segments.joinToString("/")}", TAG)
                    null
                }
            }
        }
    }
}
